import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { GoogleOAuthProvider } from '@react-oauth/google';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { ConsultationProvider } from './contexts/ConsultationContext';
import { ChatProvider } from './contexts/ChatContext';
import { AstrologyProvider } from './contexts/AstrologyContext';
import { ShopProvider } from './contexts/ShopContext';
import { CallProvider } from './contexts/CallContext';

// Analytics
import AnalyticsProvider, { AnalyticsAutoTracking } from './analytics/AnalyticsProvider';
import ConsentBanner from './analytics/ConsentBanner';
import config from './config';

// Import routes configuration
import routes from './routes';
import ToastNotification from './components/notifications/ToastNotification';
import FCMManager from './components/notifications/FCMManager';
import { useAuth } from './contexts/AuthContext';
import GlobalCallPiP from './components/call/GlobalCallPiP';
import NetworkStatus from './components/common/NetworkStatus';
import UpdateToast from './components/common/UpdateToast';
import InstallBanner from './components/common/InstallBanner';

import './styles/global.css';
import './styles/auth.css';

const AuthenticatedFCM = () => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <FCMManager /> : null;
};

function App() {
  // Create theme
  const theme = createTheme({
    palette: {
      primary: {
        main: '#B8860B',
        light: '#DAA520',
        dark: '#8B6914',
      },
      secondary: {
        main: '#DAA520',
        light: '#FFD700',
        dark: '#996515',
      },
      background: {
        default: '#FFFDF7',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#2C1810',
        secondary: '#6B5744',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontFamily: '"Playfair Display", Georgia, serif' },
      h2: { fontFamily: '"Playfair Display", Georgia, serif' },
      h3: { fontFamily: '"Playfair Display", Georgia, serif' },
      h4: { fontFamily: '"Playfair Display", Georgia, serif' },
      h5: { fontFamily: '"Playfair Display", Georgia, serif' },
      h6: { fontFamily: '"Playfair Display", Georgia, serif' },
    },
  });

  return (
    <GoogleOAuthProvider clientId={config.google.clientId}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AnalyticsProvider config={config.analytics}>
        <AuthProvider>
          <ConsultationProvider>
            <CallProvider>
            <ChatProvider>
              <AstrologyProvider>
                <ShopProvider>
                  <Router>
                    <AnalyticsAutoTracking />
                    <AuthenticatedFCM />
                    <div className="App">
                      <NetworkStatus />
                      <Routes>
                        {routes.map((route, index) => (
                          <Route
                            key={index}
                            path={route.path}
                            element={route.element}
                          />
                        ))}
                      </Routes>
                      <GlobalCallPiP />
                      {/* ConsentBanner hidden — consent bypassed while validating analytics e2e */}
                      {/* <ConsentBanner language="en" /> */}
                      <ToastNotification />
                      <UpdateToast />
                      <InstallBanner />
                    </div>
                  </Router>
                </ShopProvider>
              </AstrologyProvider>
            </ChatProvider>
            </CallProvider>
          </ConsultationProvider>
        </AuthProvider>
      </AnalyticsProvider>
    </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
